import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_THEME, type ClientTheme } from './default-theme';
import { fetchClientTheme } from '../api';

/**
 * Per-product theming — client-side resolver.
 *
 * The server is the single source of truth for theme data. On mount, this
 * provider reads `client_id` from the URL query params and fetches the
 * resolved theme from `/api/v1/clients/:clientId/theme`. Until the fetch
 * resolves (or if it fails), it serves `DEFAULT_THEME` so every page
 * always paints with something sensible.
 *
 * Hard rule: never hardcode a per-client theme object here. The themeMap
 * pattern from earlier revisions violated the Client Onboarding Principle
 * and has been removed.
 */

const ThemeContext = createContext<ClientTheme>(DEFAULT_THEME);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('client_id');
  const [theme, setTheme] = useState<ClientTheme>(DEFAULT_THEME);

  useEffect(() => {
    if (!clientId) {
      setTheme(DEFAULT_THEME);
      return;
    }

    let cancelled = false;
    void fetchClientTheme(clientId).then((resolved) => {
      if (!cancelled) {
        setTheme(resolved);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Apply theme colors as CSS custom properties so existing pages can
  // reference `var(--theme-primary)` without prop-drilling. Using
  // useMemo to avoid re-computing the style on every render.
  const style = useMemo(
    () =>
      ({
        '--theme-primary': theme.colors.primary,
        '--theme-primary-hover': theme.colors.primaryHover,
        '--theme-background': theme.colors.background,
        '--theme-text': theme.colors.text,
        '--theme-text-muted': theme.colors.textMuted,
        minHeight: '100vh',
        background: 'var(--theme-background)',
        color: 'var(--theme-text)',
      }) as React.CSSProperties,
    [theme],
  );

  return (
    <ThemeContext.Provider value={theme}>
      <div style={style}>{children}</div>
    </ThemeContext.Provider>
  );
}

export function useTheme(): ClientTheme {
  return useContext(ThemeContext);
}

export type { ClientTheme } from './default-theme';
